import type { EvaluationContext } from '@openfeature/core';
import { z } from 'zod';

/**
 * The application-level context every flag evaluation must carry.
 *
 * `orgId` is mandatory — it becomes the OpenFeature `targetingKey`, and all
 * current targeting rules (Datadog and flagd) key off it. `userId` / `tenant`
 * are optional extra dimensions for finer-grained rules. `env` is the Datadog
 * `env` dimension (derive via `toDatadogEnv` from `@risksmart-app/shared`:
 * server from `DD_ENV`, web from `REACT_APP_ENVIRONMENT`) so per-environment
 * rules resolve identically on web and server.
 */
export const featureFlagContextSchema = z
  .object({
    orgId: z.string().min(1),
    userId: z.string().min(1).optional(),
    tenant: z.string().min(1).optional(),
    env: z.string().min(1).optional(),
  })
  .strict();

export type FeatureFlagContext = z.infer<typeof featureFlagContextSchema>;

/**
 * Validates the application context at runtime and maps it onto the
 * OpenFeature {@link EvaluationContext}. Throws a `ZodError` when the caller
 * passes malformed data (empty orgId, unexpected keys, wrong types) so bad
 * targeting input is surfaced instead of silently evaluating to defaults.
 *
 * `targetingKey = orgId` is a deliberate decision (reversing the userId-based
 * key explored in PR #6843): flags must resolve org-consistently — every user
 * in an organisation sees the same value, and percentage rollouts bucket whole
 * organisations. Per-user gradual rollout is intentionally unsupported;
 * gradual delivery happens org-by-org via `orgId` targeting rules.
 */
export const toEvaluationContext = (
  context: FeatureFlagContext
): EvaluationContext => {
  const parsed = featureFlagContextSchema.parse(context);

  return {
    targetingKey: parsed.orgId,
    orgId: parsed.orgId,
    ...(parsed.userId !== undefined && { userId: parsed.userId }),
    ...(parsed.tenant !== undefined && { tenant: parsed.tenant }),
    ...(parsed.env !== undefined && { env: parsed.env }),
  };
};
